import { useEffect, useState } from 'react'
import { X, Check } from 'lucide-react'
import AssignablePropertiesFields from './AssignablePropertiesFields'
import type { AssignableFields, AssignableSquad, AssignableMember } from './AssignablePropertiesFields'
import { MARKERS, isSideMarker, isTeamMarker, teamOf, teamSrc } from './markers'
import {
  ChoiceField, Field, SliderField, TeamField, editorLabel, editorMenu, lineStyleOptions,
} from './editorUi'
import { useLanguage } from '@/contexts/LanguageContext'

type LineStyle = 'solid' | 'dashed' | 'dotted'
type ArrowHead = 'none' | 'arrow' | 'flat'

export interface VehicleRef {
  id: number
  name: string
  speed: number
}

export interface ArrowFields {
  color: string
  sw: number
  ls: LineStyle
  ah: ArrowHead
  title: string
  text: string
  squads: AssignableSquad[]
  members: AssignableMember[]
  startMarkerSrc: string | null
  vehicle: VehicleRef | null
}

interface Props {
  arrow: ArrowFields
  squadPresets?: { name: string; color: string }[]
  onUpdate: (patch: Partial<ArrowFields>) => void
}

export default function ArrowPropertiesPanel({ arrow, squadPresets, onUpdate }: Props) {
  const { T } = useLanguage()
  const [vehicles, setVehicles] = useState<VehicleRef[]>([])
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false)
  // Izgara beyaz asılları listeler; seçili simge takım kopyası olabilir.
  const startBase = arrow.startMarkerSrc ? teamSrc(arrow.startMarkerSrc, null) : null
  const startTeam = arrow.startMarkerSrc ? teamOf(arrow.startMarkerSrc) : null

  useEffect(() => {
    fetch('/api/vehicle-presets', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((data: VehicleRef[]) => setVehicles(data))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!showVehicleDropdown) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest?.('[data-vehicle-dropdown]')) return
      setShowVehicleDropdown(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showVehicleDropdown])

  const fields: AssignableFields = {
    title: arrow.title,
    text: arrow.text,
    color: arrow.color,
    squads: arrow.squads,
    members: arrow.members,
  }

  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-4">
      <AssignablePropertiesFields
        fields={fields}
        squadPresets={squadPresets}
        onFieldsChange={patch => onUpdate(patch)}
      />

      <SliderField
        label={T.strategy.strokeWidth}
        display={`${arrow.sw}px`}
        value={arrow.sw} min={0.5} max={8} step={0.5}
        onChange={v => onUpdate({ sw: v })}
      />

      <ChoiceField
        label={T.strategy.lineStyle}
        value={arrow.ls}
        options={lineStyleOptions(T.strategy)}
        onChange={v => onUpdate({ ls: v })}
      />

      <ChoiceField
        label={T.strategy.arrowHead}
        value={arrow.ah}
        options={[
          { value: 'none' as ArrowHead, label: T.strategy.none, preview: (
            <svg width="36" height="12" viewBox="0 0 36 12" aria-hidden>
              <line x1="2" y1="6" x2="34" y2="6" stroke="currentColor" strokeWidth="2" />
            </svg>
          ) },
          { value: 'arrow' as ArrowHead, label: T.strategy.arrow, preview: (
            <svg width="36" height="12" viewBox="0 0 36 12" aria-hidden>
              <line x1="2" y1="6" x2="28" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="22" y1="2" x2="34" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="22" y1="10" x2="34" y2="6" stroke="currentColor" strokeWidth="2" />
            </svg>
          ) },
          { value: 'flat' as ArrowHead, label: T.strategy.flat, preview: (
            <svg width="36" height="12" viewBox="0 0 36 12" aria-hidden>
              <line x1="2" y1="6" x2="26" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="28" y1="1" x2="28" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) },
        ]}
        onChange={v => onUpdate({ ah: v })}
      />

      {/* Başlangıç işaretçisi — okun çıktığı yere konan simge */}
      <Field label={T.strategy.startMarker}>
        <div className="grid grid-cols-6 gap-1">
          <button
            onClick={() => onUpdate({ startMarkerSrc: null })}
            title={T.strategy.none}
            className={`flex h-9 w-full cursor-pointer items-center justify-center rounded-sm border transition-colors ${
              !arrow.startMarkerSrc
                ? 'border-primary/60 bg-primary/15'
                : 'border-white/10 bg-white/5 hover:bg-white/10'
            }`}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
          {MARKERS.map(m => (
            <button
              key={m.id}
              onClick={() => onUpdate({
                startMarkerSrc: startBase === m.src ? null : teamSrc(m.src, startTeam),
              })}
              title={m.label}
              className={`flex h-9 w-full cursor-pointer items-center justify-center rounded-sm border transition-colors ${
                startBase === m.src
                  ? 'border-primary/60 bg-primary/15'
                  : 'border-white/10 bg-white/5 hover:bg-white/10'
              }`}
            >
              <img src={teamSrc(m.src, startTeam)} alt={m.label} className={`h-5 object-contain ${isSideMarker(m.src) ? 'w-8' : 'w-5'}`} />
            </button>
          ))}
        </div>
      </Field>
      {arrow.startMarkerSrc && isTeamMarker(arrow.startMarkerSrc) && (
        <TeamField
          label={T.strategy.team}
          noneLabel={T.strategy.teamNone}
          value={startTeam}
          onChange={team => onUpdate({ startMarkerSrc: teamSrc(arrow.startMarkerSrc!, team) })}
        />
      )}

      {/* Araç seçici */}
      <div className="relative">
        <p className={`mb-2 ${editorLabel}`}>{T.strategy.vehicle}</p>
        {arrow.vehicle && (
          <div className="flex items-center gap-1 mb-2">
            <span className="inline-flex items-center gap-1 rounded-sm border border-white/10 bg-white/5 px-2 py-0.5 text-xs">
              {arrow.vehicle.name}
              <span className="text-foreground/50">· {arrow.vehicle.speed} km/h</span>
              <button
                onClick={() => onUpdate({ vehicle: null })}
                className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}
        <button
          onClick={() => { setShowVehicleDropdown(v => !v) }}
          className="h-8 w-full cursor-pointer rounded-sm border border-white/10 bg-white/5 px-2 text-left text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
        >
          {arrow.vehicle ? T.strategy.changeVehicle : T.strategy.selectVehicle}
        </button>

        {/* Vehicle dropdown — positioned relative to this section */}
        {showVehicleDropdown && (
          <div
            data-vehicle-dropdown
            className={`absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto ${editorMenu}`}
          >
            {vehicles.length === 0 && (
              <div className="px-3 py-2 text-xs text-foreground/40">{T.strategy.noVehiclePresets}</div>
            )}
            {vehicles.map(v => {
              const isSelected = arrow.vehicle?.id === v.id
              return (
                <button
                  key={v.id}
                  onClick={() => { onUpdate({ vehicle: { id: v.id, name: v.name, speed: v.speed } }); setShowVehicleDropdown(false) }}
                  className="flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 text-xs text-foreground transition-colors hover:bg-white/10"
                >
                  <span className="flex-1 truncate text-left">{v.name}</span>
                  <span className="text-foreground/40">{v.speed} km/h</span>
                  {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
